import { GlobalLogger } from "../scanview/debugLogger";

export type Thresholds = { cardThreshold: number; laplacianThreshold: number };

export type ScanAttempt = {
  timestamp: number;
  elapsed: number;
  cardScore: number;
  laplacianValue: number;
  captured: boolean;
  adjustmentCount: number;
  thresholds: Thresholds;
  side: Side;
};

export type Side = "front" | "back";

export type AdaptiveThresholdProps = {
  initialCardThreshold?: number;
  initialLaplacianThreshold?: number;
  minCardThreshold?: number;
  minLaplacianThreshold?: number;
  backsideMultiplier?: number;
  stabilizationPeriod?: number;
  adjustmentInterval?: number;
  reductionPerInterval?: number;
  maxAdjustments?: number;
  minAttemptsToReduceFront?: number;
  minAttemptsToReduceBack?: number;
};

export default class AdaptiveThreshold {
  private initialThresholds: Thresholds;
  public currentThresholds: Thresholds;
  private intervalId?: number;

  private stabilizationPeriod: number;
  private adjustmentInterval: number;
  private maxAdjustments: number;
  private minAttemptsToReduceFront: number;
  private minAttemptsToReduceBack: number;

  private backsideMultiplier: number;

  private minCardThreshold: number;
  private minLaplacianThreshold: number;

  private scanAttempts: ScanAttempt[] = [];
  private scanStartTime = 0;
  private isScanning = false;
  private adjustmentCount = 0;
  private currentSide: Side = "front";
  private adjustmentMultiplier: number;
  private stabilizationTimerId?: number;
  private didCapture = false;
  private intervalCount = 0;
  private now = () => performance.now();

  private frontSuccessThresholds?: Thresholds;

  constructor({
    initialCardThreshold,
    adjustmentInterval,
    backsideMultiplier,
    initialLaplacianThreshold,
    minCardThreshold,
    minLaplacianThreshold,
    reductionPerInterval,
    stabilizationPeriod,
    maxAdjustments,
    minAttemptsToReduceFront,
    minAttemptsToReduceBack,
  }: AdaptiveThresholdProps) {
    this.currentThresholds = {
      cardThreshold: initialCardThreshold || 0.9,
      laplacianThreshold: initialLaplacianThreshold || 0.01,
    };
    this.initialThresholds = {
      cardThreshold: initialCardThreshold || 0.9,
      laplacianThreshold: initialLaplacianThreshold || 0.01,
    };
    this.stabilizationPeriod = stabilizationPeriod || 2000;
    this.adjustmentInterval = adjustmentInterval || 2000;
    this.adjustmentMultiplier = 1 - (reductionPerInterval || 0.15);
    this.backsideMultiplier = backsideMultiplier || 0.95;
    this.maxAdjustments = maxAdjustments || 5;
    this.minCardThreshold = minCardThreshold || 0.5;
    this.minLaplacianThreshold = minLaplacianThreshold || 0.005;
    this.minAttemptsToReduceFront = minAttemptsToReduceFront || 10;
    this.minAttemptsToReduceBack = minAttemptsToReduceBack || 10;
  }

  startSession(side: Side = "front") {
    this.clearTimers();

    this.currentSide = side;
    this.isScanning = true;
    this.didCapture = false;
    this.adjustmentCount = 0;
    this.scanStartTime = this.now();

    this.currentThresholds =
      side === "back" && this.frontSuccessThresholds
        ? {
            cardThreshold:
              this.frontSuccessThresholds.cardThreshold *
              this.backsideMultiplier,
            laplacianThreshold:
              this.frontSuccessThresholds.laplacianThreshold *
              this.backsideMultiplier,
          }
        : { ...this.initialThresholds };

    GlobalLogger.debug(`${side} session started`, "verbose");
    GlobalLogger.debug(
      `Initial thresholds ${JSON.stringify(this.currentThresholds)}`,
      "verbose"
    );

    this.stabilizationTimerId = window.setTimeout(() => {
      this.intervalId = window.setInterval(
        () => this.tick(),
        this.adjustmentInterval
      );
    }, this.stabilizationPeriod);
  }

  getCurrent() {
    return this.currentThresholds;
  }

  stopSession() {
    this.clearTimers();

    if (
      this.currentSide === "front" &&
      !this.frontSuccessThresholds &&
      this.scanAttempts.length
    ) {
      const firstGood = this.scanAttempts.find((a) => a.captured);
      if (firstGood) {
        this.frontSuccessThresholds = { ...firstGood.thresholds };
      }
    }
    GlobalLogger.debug(`Session stopped (${this.currentSide})`, "verbose");
    GlobalLogger.debug(
      `Final thresholds for ${this.currentSide} side ${JSON.stringify(
        this.getCurrent()
      )}`,
      "verbose"
    );

    this.isScanning = false;
    this.didCapture = false;
    this.adjustmentCount = 0;
    this.intervalCount = 0;
    this.scanAttempts = [];
  }

  recordAttempt(cardScore: number, laplacian: number, captured: boolean) {
    const attempt: ScanAttempt = {
      adjustmentCount: this.adjustmentCount,
      captured: captured,
      cardScore: cardScore,
      elapsed: this.now() - this.scanStartTime,
      laplacianValue: laplacian,
      side: this.currentSide,
      thresholds: this.getCurrent(),
      timestamp: Date.now(),
    };

    this.scanAttempts.push(attempt);

    if (this.scanAttempts.length > 100) {
      this.scanAttempts.shift();
    }

    if (!this.didCapture && captured === true) {
      GlobalLogger.debug("ATTEMPT CAPTURED");
      this.didCapture = captured;
    }

    GlobalLogger.debug(
      `Attempt ${captured ? "✓" : "✗"}  score=${cardScore.toFixed(
        3
      )} lap=${laplacian.toFixed(4)}`,
      "verbose"
    );
  }

  private reduce() {
    this.currentThresholds = {
      cardThreshold: Math.max(
        this.minCardThreshold,
        this.currentThresholds.cardThreshold * this.adjustmentMultiplier
      ),
      laplacianThreshold: Math.max(
        this.minLaplacianThreshold,
        this.currentThresholds.laplacianThreshold * this.adjustmentMultiplier
      ),
    };
    GlobalLogger.debug(
      `Thresholds reduced → card ${this.currentThresholds.cardThreshold.toFixed(
        3
      )}, lap ${this.currentThresholds.laplacianThreshold.toFixed(4)}`,
      "verbose"
    );
  }

  private tick() {
    this.intervalCount++;
    if (!this.isScanning) return;

    const minAttempts =
      this.currentSide === "front"
        ? this.minAttemptsToReduceFront
        : this.minAttemptsToReduceBack;

    if (
      !this.didCapture &&
      this.adjustmentCount < this.maxAdjustments &&
      this.scanAttempts.length >= minAttempts
    ) {
      this.reduce();
      this.adjustmentCount++;
    }

    this.didCapture = false;
  }

  private clearTimers() {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    if (this.stabilizationTimerId !== undefined) {
      clearTimeout(this.stabilizationTimerId);
      this.stabilizationTimerId = undefined;
    }
  }
}
